import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:util:cpt-extraction');

/**
 * Smart CPT Code Extraction Utility
 *
 * Extracts CPT codes from various document formats using multiple strategies:
 * - Basic regex pattern matching
 * - Context-aware extraction (near keywords like "CPT", "code", "procedure")
 * - Format-specific handling (with modifiers, parentheses, quotes)
 * - Range validation (filters out invalid codes like ZIP codes)
 * - False positive filtering (dates, prices, etc.)
 */

/**
 * CPT code ranges for validation
 * CPT codes are 5-digit codes in specific ranges:
 * - Category I: 00100-99999 (most common procedures)
 * - Category II: Performance measurement codes (0001F-9999F)
 * - Category III: Emerging technology (0001T-9999T)
 *
 * We focus on Category I codes (5-digit numeric only)
 */
const CPT_VALID_RANGES = [
  { min: 100, max: 99999 }, // All Category I codes
];

/**
 * Common CPT code ranges (helps score/prioritize matches)
 */
const CPT_COMMON_RANGES = [
  { min: 90000, max: 99999, category: 'Evaluation & Management / Medicine' },
  { min: 70000, max: 79999, category: 'Radiology' },
  { min: 80000, max: 89999, category: 'Pathology & Laboratory' },
  { min: 40000, max: 49999, category: 'Surgery - Digestive' },
  { min: 10000, max: 19999, category: 'Surgery - Integumentary' },
  { min: 33000, max: 37999, category: 'Surgery - Cardiovascular' },
];

/**
 * Keywords that often appear near CPT codes in documents
 * Enhanced with medical billing and insurance terminology
 */
const CPT_KEYWORDS = [
  // Core CPT terms
  'cpt',
  'code',
  'procedure',
  'service',
  'billing',
  'charge',
  'claim',
  'dx',
  'diagnosis',
  'treatment',

  // Medical billing specific
  'hcpcs',
  'modifier',
  'revenue',
  'charges',
  'billed',
  'eob',
  'remittance',
  'allowed',
  'reimbursement',
  'encounter',
  'superbill',
  'ub-04',
  'cms-1500',

  // Insurance/claim terms
  'paid',
  'denied',
  'adjustment',
  'copay',
  'deductible',
  'coinsurance',
  'balance',
  'patient responsibility',

  // Provider/practice terms
  'provider',
  'physician',
  'practitioner',
  'visit',
  'consultation',
  'exam',
  'evaluation',
  'management',
];

/**
 * Patterns that indicate a number is NOT a CPT code
 * Enhanced with PDF-specific and document artifact patterns
 */
const FALSE_POSITIVE_PATTERNS = [
  // General false positives
  /^\d{5}-\d{4}$/, // ZIP+4 format (12345-6789)
  /\$\s*\d{5}/, // Prices ($12345)
  /\d{2}\/\d{2}\/\d{2}/, // Dates near the number

  // PDF-specific document artifacts
  /page\s+\d+\s+of\s+\d{5}/i, // "Page 1 of 12345"
  /invoice\s*#?\s*\d{5}/i, // "Invoice #12345"
  /account\s*#?\s*\d{5}/i, // "Account #54321"
  /member\s*id\s*:?\s*\d{5}/i, // "Member ID: 67890"
  /patient\s*id\s*:?\s*\d{5}/i, // "Patient ID: 12345"
  /reference\s*#?\s*\d{5}/i, // "Reference #99999"

  // Phone numbers (5 digits is part of area code + prefix)
  /\d{3}[-.\s]\d{3}[-.\s]\d{4}/, // Phone: 123-456-7890

  // Addresses/ZIP codes
  /\b\d{5}\s*,?\s*[A-Z]{2}\b/, // "12345, CA" or "12345 NY"

  // Financial identifiers
  /policy\s*#?\s*\d{5}/i, // "Policy #12345"
  /auth\s*#?\s*\d{5}/i, // "Auth #54321"
];

/**
 * Check if a 5-digit code is in a valid CPT range
 */
function isValidCptRange(code: string): boolean {
  const num = parseInt(code, 10);
  return CPT_VALID_RANGES.some(range => num >= range.min && num <= range.max);
}

/**
 * Check if a code is likely a common CPT code
 */
function isCommonCptRange(code: string): boolean {
  const num = parseInt(code, 10);
  return CPT_COMMON_RANGES.some(range => num >= range.min && num <= range.max);
}

/**
 * Check if a number looks like a sequential/test pattern (likely not a real CPT code)
 * Examples: 12345, 54321, 11111, 99999, 00000
 */
function looksLikeSequentialNumber(code: string): boolean {
  const digits = code.split('').map(d => parseInt(d));

  // All same digit (11111, 99999, 00000)
  if (new Set(digits).size === 1) {
    logger.info(`🔍 [CPT-FILTER] Rejected ${code}: all same digit`);
    return true;
  }

  // Sequential ascending (12345, 01234)
  const isAscending = digits.every((d, i) => {
    const prev = digits[i - 1];
    return i === 0 || (prev !== undefined && d === (prev + 1) % 10);
  });

  // Sequential descending (54321, 98765)
  const isDescending = digits.every((d, i) => {
    const prev = digits[i - 1];
    return i === 0 || (prev !== undefined && d === (prev - 1 + 10) % 10);
  });

  if (isAscending || isDescending) {
    logger.info(`🔍 [CPT-FILTER] Rejected ${code}: sequential pattern`);
    return true;
  }

  return false;
}

/**
 * Check if context around a number suggests it's a CPT code
 */
function hasContextualEvidence(text: string, matchIndex: number, matchLength: number): boolean {
  // Look at 50 characters before and after the match
  const contextBefore = text.substring(Math.max(0, matchIndex - 50), matchIndex).toLowerCase();
  const contextAfter = text
    .substring(matchIndex + matchLength, Math.min(text.length, matchIndex + matchLength + 50))
    .toLowerCase();

  const context = contextBefore + contextAfter;

  // Check if any CPT-related keywords appear nearby
  return CPT_KEYWORDS.some(keyword => context.includes(keyword));
}

/**
 * Strategy 1: Extract codes with modifiers (99213-25, 99213-GT)
 * High confidence - codes with modifiers are almost always CPT codes
 */
function extractCodesWithModifiers(text: string): string[] {
  // CPT codes with modifiers: 99213-25, 45378-59, etc.
  const regex = /\b(\d{5})[-\s]?(?:[A-Z]{2}|\d{2})\b/gi;
  const matches = text.matchAll(regex);
  const codes: string[] = [];

  for (const match of matches) {
    const code = match[1];
    if (code && isValidCptRange(code) && !looksLikeSequentialNumber(code)) {
      codes.push(code);
    }
  }

  return codes;
}

/**
 * Strategy 2: Extract codes near CPT keywords
 * Medium-high confidence - codes near billing keywords are likely CPT codes
 */
function extractCodesNearKeywords(text: string): string[] {
  const regex = /\b\d{5}\b/g;
  const matches = [...text.matchAll(regex)];
  const codes: string[] = [];

  for (const match of matches) {
    const code = match[0];
    const index = match.index || 0;

    // Check if code is in valid range, has contextual evidence, and not sequential
    if (
      isValidCptRange(code) &&
      !looksLikeSequentialNumber(code) &&
      hasContextualEvidence(text, index, code.length)
    ) {
      codes.push(code);
    }
  }

  return codes;
}

/**
 * Strategy 3: Extract codes in common formats
 * - Parentheses: (99213)
 * - Quotes: "99213", '99213'
 * - Colons: CPT: 99213, Code: 99213
 * - Comma-separated: 99213, 99214, 99215
 * Medium confidence - formatting suggests intentional code listing
 */
function extractCodesInCommonFormats(text: string): string[] {
  const codes: string[] = [];

  // Pattern 1: In parentheses
  const parenRegex = /\((\d{5})\)/g;
  for (const match of text.matchAll(parenRegex)) {
    const code = match[1];
    if (code && isValidCptRange(code) && !looksLikeSequentialNumber(code)) {
      codes.push(code);
    }
  }

  // Pattern 2: In quotes
  const quoteRegex = /["'](\d{5})["']/g;
  for (const match of text.matchAll(quoteRegex)) {
    const code = match[1];
    if (code && isValidCptRange(code) && !looksLikeSequentialNumber(code)) {
      codes.push(code);
    }
  }

  // Pattern 3: After colons/labels
  const colonRegex = /(?:cpt|code|procedure|service)[\s:]+(\d{5})/gi;
  for (const match of text.matchAll(colonRegex)) {
    const code = match[1];
    if (code && isValidCptRange(code) && !looksLikeSequentialNumber(code)) {
      codes.push(code);
    }
  }

  return codes;
}

/**
 * Strategy 4: Aggressive extraction with filtering
 * Find all 5-digit numbers, then filter out obvious false positives
 * Lower confidence - catches anything that passes validation
 */
function extractAllWithFiltering(text: string): string[] {
  const regex = /\b\d{5}\b/g;
  const matches = [...text.matchAll(regex)];
  const codes: string[] = [];

  for (const match of matches) {
    const code = match[0];
    const index = match.index || 0;

    // Skip if not in valid range
    if (!isValidCptRange(code)) continue;

    // Skip if sequential/test pattern
    if (looksLikeSequentialNumber(code)) continue;

    // Check for false positive patterns in surrounding text
    const contextStart = Math.max(0, index - 20);
    const contextEnd = Math.min(text.length, index + code.length + 20);
    const context = text.substring(contextStart, contextEnd);

    const isFalsePositive = FALSE_POSITIVE_PATTERNS.some(pattern => pattern.test(context));

    if (!isFalsePositive) {
      codes.push(code);
    }
  }

  return codes;
}

/**
 * Main extraction function - combines all strategies with enhanced filtering
 *
 * Enhancements:
 * - Expanded medical billing keyword set (40+ terms)
 * - PDF-specific false positive filtering (page numbers, IDs, etc.)
 * - Sequential number detection (12345, 99999, etc.)
 * - Multi-strategy scoring system
 */
export function extractCptCodesFromText(text: string): string[] {
  logger.info('🔍 [CPT-EXTRACTION] Starting enhanced smart extraction...');
  logger.info('📄 [CPT-EXTRACTION] Text length:', text.length, 'characters');

  // Run all extraction strategies
  const strategy1 = extractCodesWithModifiers(text);
  const strategy2 = extractCodesNearKeywords(text);
  const strategy3 = extractCodesInCommonFormats(text);
  const strategy4 = extractAllWithFiltering(text);

  logger.info('📊 [CPT-EXTRACTION] Strategy results:', {
    withModifiers: strategy1.length,
    nearKeywords: strategy2.length,
    commonFormats: strategy3.length,
    filteredAll: strategy4.length,
  });

  // Combine all results and deduplicate
  const allCodes = [...strategy1, ...strategy2, ...strategy3, ...strategy4];
  const uniqueCodes = Array.from(new Set(allCodes));

  // Score each code based on evidence strength
  const scoredCodes = uniqueCodes.map(code => {
    let score = 0;

    // +3 if found with modifier (very strong evidence)
    if (strategy1.includes(code)) score += 3;

    // +2 if found near keywords (strong evidence)
    if (strategy2.includes(code)) score += 2;

    // +1 if found in common format (moderate evidence)
    if (strategy3.includes(code)) score += 1;

    // +1 if in common CPT range (weak evidence, but helpful)
    if (isCommonCptRange(code)) score += 1;

    return { code, score };
  });

  // Sort by score (highest first) and extract codes
  const sortedCodes = scoredCodes.sort((a, b) => b.score - a.score).map(item => item.code);

  logger.info('✅ [CPT-EXTRACTION] Extraction complete:', {
    totalUnique: sortedCodes.length,
    highConfidence: scoredCodes.filter(c => c.score >= 3).length,
    mediumConfidence: scoredCodes.filter(c => c.score === 2).length,
    lowConfidence: scoredCodes.filter(c => c.score === 1).length,
    topCodes: scoredCodes
      .slice(0, 10)
      .map(c => `${c.code}(${c.score})`)
      .join(', '),
  });

  return sortedCodes;
}
